import { Platform } from 'react-native';
// ΣΗΜΕΙΩΣΗ: το native binary της εφαρμογής είναι χτισμένο με τη ΝΕΑ
// class-based API (File/Directory/Paths) του expo-file-system — το legacy
// bridge (readAsStringAsync/downloadAsync) δεν υπάρχει καν compiled στο
// native module, οπότε πετάει UnavailabilityError ό,τι JS αλλαγή κι αν γίνει
// μέσω OTA. Χρησιμοποιούμε αποκλειστικά τη νέα API.
import { File, Paths } from 'expo-file-system';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import { Asset } from 'expo-asset';

import type { Agreement, AgreementType, Profile } from '@/lib/database.types';
import {
  commitmentIntro,
  counterpartyDisplayName,
  documentIntegrityCode,
  formatAgreementAmount,
  formatAmount,
  formatDate,
  formatDateTime,
  formatUserId,
  signatureCode,
  statusLabel,
} from '@/lib/format';
import { qrSvg } from '@/lib/qr';
import { parseSignature } from '@/lib/signature';
import { agreementVerifyUrl } from '@/lib/verify';
import { fetchSigningEvents, type SigningMeta } from '@/lib/signingEvents';
import type { AgreementParty } from '@/lib/multiParty';
import { computeLineItemsTotals, lineItemDiscountAmount, lineItemTotal, type OfferLineItem } from '@/lib/offers';

/** Επίσημος αριθμός εγγράφου της συμφωνίας (ίδια μορφή με τις αποδείξεις). */
export function agreementDocNumber(id: string): string {
  return `ΧΕΙΡ-${id.slice(0, 8).toUpperCase()}`;
}

/** Διαφυγή χαρακτήρων ώστε περιεχόμενο χρήστη να μην σπάει/εισάγει HTML. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Μετατρέπει το ελεύθερο κείμενο των όρων συνεργασίας του επαγγελματία σε
 * ασφαλές HTML (κουκίδες/αρίθμηση), ΧΩΡΙΣ ΠΟΤΕ να ερμηνεύσει το περιεχόμενο
 * του χρήστη ως HTML. Κάθε γραμμή κειμένου περνά πάντα από escapeHtml — η
 * μόνη «μορφοποίηση» που αναγνωρίζεται είναι το πρόθεμα της γραμμής
 * («- », «* », «1. » κ.λπ.), το οποίο αφαιρείται πριν το escape και
 * μεταφράζεται σε δικά μας, ελεγχόμενα tags (<ul>/<ol>/<li>/<p>). Έτσι
 * είναι αρχιτεκτονικά αδύνατο να περάσει <script>, onclick, iframe ή
 * οποιοδήποτε άλλο ενεργό περιεχόμενο μέσα από αυτό το πεδίο — δεν υπάρχει
 * μονοπάτι όπου ωμό HTML από τον χρήστη καταλήγει στην έξοδο.
 */
function safeTermsToHtml(raw: string): string {
  const lines = raw.replace(/\r\n/g, '\n').split('\n');
  const blocks: string[] = [];
  let listBuffer: string[] = [];
  let listType: 'ul' | 'ol' | null = null;

  function flushList() {
    if (listBuffer.length === 0) return;
    const tag = listType === 'ol' ? 'ol' : 'ul';
    blocks.push(`<${tag} style="margin:4px 0 4px 18px;padding:0;">${listBuffer.join('')}</${tag}>`);
    listBuffer = [];
    listType = null;
  }

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) { flushList(); continue; }

    const bulletMatch = line.match(/^[-*•]\s+(.*)$/);
    const numberMatch = line.match(/^\d+[.)]\s+(.*)$/);

    if (bulletMatch) {
      if (listType !== 'ul') flushList();
      listType = 'ul';
      listBuffer.push(`<li>${escapeHtml(bulletMatch[1])}</li>`);
    } else if (numberMatch) {
      if (listType !== 'ol') flushList();
      listType = 'ol';
      listBuffer.push(`<li>${escapeHtml(numberMatch[1])}</li>`);
    } else {
      flushList();
      blocks.push(`<p style="margin:4px 0;">${escapeHtml(line)}</p>`);
    }
  }
  flushList();
  return blocks.join('');
}

/**
 * Όνομα συμβαλλομένου για εμφάνιση στο PDF. Πολλοί χρήστες που εγγράφονται
 * βιαστικά (π.χ. για να αποδεχτούν μια πρόσκληση/προσφορά που έλαβαν πριν
 * έχουν λογαριασμό) δεν έχουν προλάβει να συμπληρώσουν το full_name στο
 * προφίλ τους — γι' αυτό πέφτουμε σε ολοένα και πιο γενικά αναγνωριστικά
 * αντί να δείχνουμε κατευθείαν «Άγνωστος».
 */
function partyName(
  profiles: Record<string, Profile>,
  id: string | null,
  fallbackEmail?: string | null,
): string {
  if (!id) return '—';
  const p = profiles[id] as (Profile & { nickname?: string | null }) | undefined;
  return (
    p?.full_name?.trim() ||
    p?.nickname?.trim() ||
    p?.contact_email?.trim() ||
    fallbackEmail?.trim() ||
    (p?.user_number != null ? formatUserId(p.user_number) : '') ||
    'Άγνωστος'
  );
}

type SignatureInfo = {
  name: string;
  role?: string;
  shareLabel?: string | null;
  signatureText: string | null;
  signatureSvg: string | null;
  agreed: boolean;
  agreedAt: string | null;
  verified: boolean;
  userNumber: number | null;
  signCode: string | null;
  withdrawnAt?: string | null;
  /** Συμπαγές IP/συσκευή/επαλήθευση — μέσα στο ίδιο το κουτί υπογραφής,
   * αντί για ξεχωριστό πλατύ πίνακα "Μεταδεδομένα Υπογραφής" στο τέλος. */
  metaHtml?: string;
};

function signatureBlock(s: SignatureInfo): string {
  const drawn = parseSignature(s.signatureSvg);
  const roleLabel = s.role ?? 'Συμβαλλόμενο μέρος';
  const shareHtml = s.shareLabel ? `<div class="party-share">${escapeHtml(s.shareLabel)}</div>` : '';

  if (s.withdrawnAt) {
    return `<div class="party">
        <div class="party-role">${escapeHtml(roleLabel)}</div>
        <div class="party-name">${escapeHtml(s.name)}</div>
        <div class="sig-area"></div>
        <div class="sig-unsigned">Ο/Η ${escapeHtml(s.name)} αποχώρησε από τη συμφωνία στις ${escapeHtml(formatDateTime(s.withdrawnAt))}</div>
      </div>`;
  }

  if (!s.agreed) {
    return `<div class="party">
        <div class="party-role">${escapeHtml(roleLabel)}</div>
        <div class="party-name">${escapeHtml(s.name)}</div>
        ${shareHtml}
        <div class="sig-area"></div>
        <div class="sig-unsigned">— Εκκρεμεί υπογραφή —</div>
      </div>`;
  }

  const verifiedHtml = s.verified ? ' <span class="vbadge">✓ Επαληθευμένος</span>' : '';
  const sigMarkup = drawn
    ? `<svg class="sig-draw-svg" viewBox="0 0 ${drawn.w} ${drawn.h}" xmlns="http://www.w3.org/2000/svg"><path d="${drawn.d}" fill="none" stroke="#000" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`
    : `<span class="sig-name-text">${escapeHtml(s.signatureText?.trim() || s.name)}</span>`;
  const dateHtml = s.agreedAt
    ? `<div class="sig-date">Ημ/νία υπογραφής: ${escapeHtml(formatDateTime(s.agreedAt))}</div>`
    : '';
  const idHtml = s.userNumber != null
    ? `<div class="sig-code-line">ID χρήστη: ${escapeHtml(formatUserId(s.userNumber))}</div>`
    : '';
  const codeHtml = s.signCode
    ? `<div class="sig-code-line">Κωδ. υπογραφής: ${escapeHtml(s.signCode)}</div>`
    : '';

  return `<div class="party">
      <div class="party-role">${escapeHtml(roleLabel)}</div>
      <div class="party-name">${escapeHtml(s.name)}${verifiedHtml}</div>
      ${shareHtml}
      <div class="sig-area">${sigMarkup}</div>
      <div class="sig-check">✔ Υπέγραψε ψηφιακά</div>
      ${dateHtml}
      ${idHtml}
      ${codeHtml}
      ${s.metaHtml ?? ''}
    </div>`;
}

/** Στοιχεία προκαταβολής για το PDF. */
export type PdfAdvance = {
  acceptedTotal: number;
  remaining: number;
  items: { amount: number; note: string | null; date: string }[];
};

function verificationMethodLabel(method: string): string {
  if (method === 'phone') return 'SMS (κινητό)';
  if (method === 'id')    return 'Ταυτότητα / Διαβατήριο';
  return 'Χωρίς επαλήθευση';
}

/** Συμπαγής γραμμή IP/συσκευή/επαλήθευση για μέσα στο κουτί υπογραφής —
 * αντικαθιστά τον παλιό ξεχωριστό πίνακα "Μεταδεδομένα Υπογραφής". */
function compactMetaHtml(ev: SigningMeta | undefined): string {
  if (!ev) return '';
  const deviceStr = ev.device_info
    ? `${ev.device_info['model'] ?? ''} · ${ev.device_info['platform'] ?? ''} ${ev.device_info['osVersion'] ?? ''}`.trim()
    : '—';
  return `<div class="sig-meta-compact">IP: ${escapeHtml(ev.ip_address ?? '—')} · ${escapeHtml(deviceStr)} · ${escapeHtml(verificationMethodLabel(ev.verification_method))}</div>`;
}

/** Χτίζει το HTML του πιστοποιητικού συμφωνίας (φωτεινό «έγγραφο» για εκτύπωση/κοινοποίηση). */
function buildHtml(
  agreement: Agreement,
  profiles: Record<string, Profile>,
  advance?: PdfAdvance,
  signingEvents?: SigningMeta[],
  logoBase64?: string,
  businessLogoBase64?: string | null,
  multiParties?: AgreementParty[],
): string {
  const isMultiParty = !!multiParties && multiParties.length > 0;
  const creatorProfileForTerms = profiles[agreement.creator_id] as Profile & {
    professional_terms?: string | null; business_name?: string | null;
    payment_ibans?: { bank: string | null; iban: string }[] | null;
    payment_iris?: string | null; payment_revolut_link?: string | null; payment_viva_link?: string | null;
  } | undefined;
  const professionalTerms = creatorProfileForTerms?.professional_terms?.trim() || '';
  const includeProfessionalTerms = (agreement as Agreement & { include_professional_terms?: boolean }).include_professional_terms !== false;

  // Όλοι οι τρόποι πληρωμής που έχει δηλώσει ο δημιουργός στο προφίλ του.
  const paymentRows: string[] = [];
  for (const entry of creatorProfileForTerms?.payment_ibans ?? []) {
    if (!entry?.iban?.trim()) continue;
    paymentRows.push(`<div class="cover-payment-row"><strong>${escapeHtml(entry.bank?.trim() || 'IBAN')}:</strong> ${escapeHtml(entry.iban.trim())}</div>`);
  }
  if (creatorProfileForTerms?.payment_iris?.trim()) paymentRows.push(`<div class="cover-payment-row"><strong>IRIS:</strong> ${escapeHtml(creatorProfileForTerms.payment_iris.trim())}</div>`);
  if (creatorProfileForTerms?.payment_revolut_link?.trim()) paymentRows.push(`<div class="cover-payment-row"><strong>Revolut:</strong> ${escapeHtml(creatorProfileForTerms.payment_revolut_link.trim())}</div>`);
  if (creatorProfileForTerms?.payment_viva_link?.trim()) paymentRows.push(`<div class="cover-payment-row"><strong>Viva Wallet:</strong> ${escapeHtml(creatorProfileForTerms.payment_viva_link.trim())}</div>`);
  const paymentMethodsHtml = paymentRows.length
    ? `<div class="cover-payment-box"><div class="cover-payment-label">Τρόποι Πληρωμής</div>${paymentRows.join('')}</div>`
    : '';

  const isProfessionalVersion = (professionalTerms.length > 0 || paymentRows.length > 0) && includeProfessionalTerms;
  const isGoodsOffer = (agreement as Agreement & { offer_kind?: string }).offer_kind === 'goods'
    && Array.isArray((agreement as Agreement & { line_items?: OfferLineItem[] | null }).line_items)
    && ((agreement as Agreement & { line_items?: OfferLineItem[] | null }).line_items!.length > 0);
  const businessName = creatorProfileForTerms?.business_name?.trim() || partyName(profiles, agreement.creator_id);
  const creatorProfileForDetails = profiles[agreement.creator_id] as Profile & {
    business_address?: string | null; business_city?: string | null; business_postal_code?: string | null;
    business_phone?: string | null; business_email?: string | null; business_vat_number?: string | null;
  } | undefined;
  const businessDetailLines: string[] = [];
  if (creatorProfileForDetails) {
    const addressParts = [creatorProfileForDetails.business_address?.trim(), creatorProfileForDetails.business_postal_code?.trim(), creatorProfileForDetails.business_city?.trim()].filter(Boolean);
    if (addressParts.length) businessDetailLines.push(addressParts.join(', '));
    if (creatorProfileForDetails.business_vat_number?.trim()) businessDetailLines.push(`ΑΦΜ: ${creatorProfileForDetails.business_vat_number.trim()}`);
    if (creatorProfileForDetails.business_phone?.trim()) businessDetailLines.push(`Τηλ: ${creatorProfileForDetails.business_phone.trim()}`);
    if (creatorProfileForDetails.business_email?.trim()) businessDetailLines.push(creatorProfileForDetails.business_email.trim());
  }
  const businessDetailsHtml = businessDetailLines.length
    ? `<div class="cover-business-details">${businessDetailLines.map(escapeHtml).join(' · ')}</div>`
    : '';
  const creatorName = partyName(profiles, agreement.creator_id);
  const counterpartyName = counterpartyDisplayName(
    agreement,
    agreement.counterparty_id ? partyName(profiles, agreement.counterparty_id, agreement.counterparty_email) : null,
    {
      invited: (v) => `${v} (πρόσκληση)`,
      unknown: '—',
      empty: '—',
    },
  );

  const eventBySigner = new Map((signingEvents ?? []).map((e) => [e.signer_id, e]));

  const multiPartySigs: SignatureInfo[] = isMultiParty
    ? multiParties!.map((mp) => {
        const p = mp.user_id ? profiles[mp.user_id] : undefined;
        return {
          name: mp.user_id
            ? partyName(profiles, mp.user_id)
            : mp.party_email
              ? `${mp.party_email} (πρόσκληση)`
              : '—',
          role: mp.role === 'creator' ? 'Δημιουργός' : 'Συμβαλλόμενο μέρος',
          shareLabel: `Ποσοστό: ${mp.share_percent}%`,
          signatureText: p?.signature_text ?? null,
          signatureSvg: p?.signature_svg ?? null,
          agreed: mp.has_signed,
          agreedAt: mp.signed_at,
          verified: !!p?.verified,
          userNumber: p?.user_number ?? null,
          signCode: mp.has_signed && mp.user_id ? signatureCode(agreement.id, mp.user_id, mp.signed_at) : null,
          withdrawnAt: mp.withdrawn_at ?? null,
          metaHtml: mp.has_signed && mp.user_id ? compactMetaHtml(eventBySigner.get(mp.user_id)) : '',
        };
      })
    : [];
  const multiPartyNamesJoined = isMultiParty
    ? multiPartySigs.map((s) => s.name).join(', ')
    : '';

  const creatorProfile = profiles[agreement.creator_id];
  const counterpartyProfile = agreement.counterparty_id
    ? profiles[agreement.counterparty_id]
    : undefined;
  const creatorSig: SignatureInfo = {
    name: creatorName,
    signatureText: creatorProfile?.signature_text ?? null,
    signatureSvg: creatorProfile?.signature_svg ?? null,
    agreed: agreement.creator_agreed,
    agreedAt: agreement.creator_agreed_at,
    verified: !!creatorProfile?.verified,
    userNumber: creatorProfile?.user_number ?? null,
    signCode: agreement.creator_agreed
      ? signatureCode(agreement.id, agreement.creator_id, agreement.creator_agreed_at)
      : null,
    metaHtml: agreement.creator_agreed ? compactMetaHtml(eventBySigner.get(agreement.creator_id)) : '',
  };
  const counterpartySig: SignatureInfo = {
    name: counterpartyName,
    signatureText: counterpartyProfile?.signature_text ?? null,
    signatureSvg: counterpartyProfile?.signature_svg ?? null,
    agreed: agreement.counterparty_agreed,
    agreedAt: agreement.counterparty_agreed_at,
    verified: !!counterpartyProfile?.verified,
    userNumber: counterpartyProfile?.user_number ?? null,
    signCode:
      agreement.counterparty_agreed && agreement.counterparty_id
        ? signatureCode(agreement.id, agreement.counterparty_id, agreement.counterparty_agreed_at)
        : null,
    metaHtml: agreement.counterparty_agreed && agreement.counterparty_id
      ? compactMetaHtml(eventBySigner.get(agreement.counterparty_id))
      : '',
  };
  const bothSigned = isMultiParty
    ? multiPartySigs.every((s) => s.agreed)
    : agreement.creator_agreed && agreement.counterparty_agreed;
  const integrityCode = documentIntegrityCode([
    agreement.id,
    agreement.title,
    agreement.description,
    agreement.amount,
    agreement.amount_unit,
    agreement.status,
    agreement.creator_agreed_at,
    agreement.counterparty_agreed_at,
  ]);

  // rows/rowsHtml replaced by inline gov-style table in the template

  // descriptionHtml no longer used — commitment is embedded directly in the gov-style template
  const descriptionHtml = '';

  const showAdvance =
    agreement.amount != null &&
    agreement.amount_unit !== 'percent' &&
    !!advance &&
    advance.acceptedTotal > 0;
  const advanceItemsHtml = (advance?.items ?? [])
    .map((it, idx) => {
      const label = idx === 0 ? 'Προκαταβολή' : 'Δόση/Έναντι';
      const noteHtml = it.note?.trim()
        ? `<div class="advance-note">Σημείωση: ${escapeHtml(it.note.trim())}</div>`
        : '';
      return `<div class="advance-item">
          <div class="advance-row">
            <span>${label} — ${escapeHtml(formatDateTime(it.date))}</span>
            <span class="advance-amount">${escapeHtml(formatAmount(it.amount, agreement.currency))}</span>
          </div>
          ${noteHtml}
        </div>`;
    })
    .join('');
  const advanceHtml = showAdvance
    ? `<div style="margin-bottom:10px;">
         <div class="sig-section-label">Οικονομικά — Προκαταβολή &amp; Δόσεις</div>
         <table class="advance-table">
           <tr><td class="lbl">Συνολικό ποσό</td><td>${escapeHtml(formatAmount(agreement.amount, agreement.currency))}</td>
               <td class="lbl">Σύνολο προκαταβολής</td><td class="advance-amount">${escapeHtml(formatAmount(advance.acceptedTotal, agreement.currency))}</td></tr>
           <tr><td class="lbl">Υπόλοιπο</td><td colspan="3">${escapeHtml(formatAmount(advance.remaining, agreement.currency))}</td></tr>
         </table>
         ${advanceItemsHtml ? `<div class="advance-list">${advanceItemsHtml}</div>` : ''}
       </div>`
    : '';

  const verifyUrl = agreementVerifyUrl(agreement.id);
  const qrMarkup = qrSvg(verifyUrl, 4);

  const generatedAt = formatDateTime(new Date().toISOString());

  // Κατάσταση σε ελληνικά για το πίνακα
  const statusRow = statusLabel(agreement.status);
  const agrType = (agreement as Agreement & { type?: AgreementType }).type;
  const typeRow = agrType === 'fixed_term'
    ? 'Από–Έως'
    : agreement.expires_at ? 'Ορισμένης διάρκειας' : 'Αορίστου διάρκειας';
  const agrStartDate = (agreement as Agreement & { start_date?: string | null }).start_date;
  const agrEndDate = (agreement as Agreement & { end_date?: string | null }).end_date;

  return `<!DOCTYPE html>
<html lang="el">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<style>
  @page { size: A4; margin: 0 12mm 18mm; }
  * { box-sizing: border-box; }
  body {
    font-family: Arial, Helvetica, sans-serif;
    color: #1a1a1a;
    margin: 0;
    padding: 0;
    background: #fff;
    font-size: 11px;
    line-height: 1.5;
    /* Γεμίζει το πλήρες ύψος μιας Α4 σελίδας (297mm - 18mm κάτω περιθώριο)
       ώστε σε σύντομες συμφωνίες το footer να "κατεβαίνει" στο κάτω μέρος
       της σελίδας αντί να αφήνει άδειο λευκό χώρο μετά από αυτό. */
    min-height: 279mm;
    display: flex;
    flex-direction: column;
  }

  /* ── Gov.gr-style header band ── */
  .gov-band {
    background: #003375;
    padding: 10px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 64px;
    overflow: visible;
  }
  .gov-band-logo { display: flex; align-items: center; gap: 8px; }
  .gov-band-brand { color: #fff; font-size: 15px; font-weight: 700; letter-spacing: 0.5px; }
  .gov-band-sub { color: rgba(255,255,255,0.7); font-size: 8px; letter-spacing: 1.5px; text-transform: uppercase; }
  .gov-band-qr { display: flex; flex-direction: column; align-items: center; gap: 2px; }
  .gov-band-qr svg { width: 44px; height: 44px; display: block; }
  .gov-band-qr-label { color: rgba(255,255,255,0.6); font-size: 6px; letter-spacing: 1px; text-transform: uppercase; }

  /* ── Μπλε γραμμή τίτλου ── */
  .doc-type-bar {
    background: #1565C0;
    color: #fff;
    text-align: center;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 3px;
    text-transform: uppercase;
    padding: 6px 0;
  }

  /* ── Κύριο σώμα ── */
  .outer { padding: 14px 18px; }

  /* ── Αριθμός εγγράφου ── */
  .doc-num-row {
    display: flex; justify-content: space-between; margin-bottom: 10px;
    font-size: 9.5px; color: #444; border-bottom: 1px solid #ddd; padding-bottom: 6px;
  }
  .doc-num { font-family: "Courier New", monospace; font-weight: 700; font-size: 10px; color: #003375; letter-spacing: 1.5px; }

  /* ── Τίτλος συμφωνίας ── */
  .agreement-title-box {
    border-left: 4px solid #003375; padding: 8px 12px; margin-bottom: 12px;
    background: #f0f4fa;
  }
  .agreement-title-label { font-size: 8.5px; text-transform: uppercase; letter-spacing: 1.5px; color: #003375; margin-bottom: 3px; font-weight: 700; }
  .agreement-title { font-size: 14px; font-weight: 700; color: #1a1a1a; }

  /* ── Πίνακας στοιχείων ── */
  .info-table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
  .info-table td { border: 1px solid #c8d8ec; padding: 4px 8px; font-size: 10px; vertical-align: top; }
  .info-table td.lbl { background: #eaf0f9; font-weight: 700; width: 28%; font-size: 9.5px; color: #003375; }
  .info-table td.val { color: #1a1a1a; }

  /* ── Κείμενο δέσμευσης ── */
  .commitment-box {
    border: 1px solid #c8d8ec; border-left: 4px solid #1565C0;
    padding: 10px 14px; margin-bottom: 10px; background: #f8fbff;
  }
  .commitment-label { font-size: 8.5px; text-transform: uppercase; letter-spacing: 1.5px; color: #003375; margin-bottom: 5px; font-weight: 700; }
  .commitment-text { font-size: 11.5px; font-style: italic; font-weight: 700; line-height: 1.6; color: #1a1a1a; font-family: "Times New Roman", serif; }
  .description-text { font-size: 11px; line-height: 1.55; margin-top: 8px; color: #333; white-space: pre-wrap; border-top: 1px dashed #c8d8ec; padding-top: 6px; }

  /* ── Υπογραφές ── */
  .sig-section-label {
    font-size: 8.5px; text-transform: uppercase; letter-spacing: 2px; color: #fff;
    background: #003375; padding: 4px 8px; margin: 12px 0 8px;
  }
  .parties { display: flex; gap: 14px; flex-wrap: wrap; }
  .party {
    flex: 0 1 200px; width: 200px; border: 1px solid #c8d8ec; border-top: 3px solid #003375; padding: 10px;
    min-height: 110px; position: relative;
  }
  .party-share { font-size: 8.5px; color: #666; margin-bottom: 4px; }
  .party-role { font-size: 8.5px; text-transform: uppercase; letter-spacing: 1.2px; color: #003375; margin-bottom: 3px; font-weight: 700; }
  .party-name { font-size: 12px; font-weight: 700; color: #1a1a1a; margin-bottom: 8px; }
  .sig-area { height: 44px; border-bottom: 1.5px solid #003375; margin-bottom: 4px; display: flex; align-items: flex-end; justify-content: center; padding-bottom: 2px; overflow: hidden; }
  .sig-name-text {
    font-family: "Snell Roundhand", "Segoe Script", "Bradley Hand", cursive;
    font-size: 18px; color: #003375; line-height: 1;
    max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .sig-draw-svg { width: 130px; height: 44px; display: block; margin: 0 auto; }
  .sig-check { font-size: 10px; color: #0a6a0a; font-weight: 700; }
  .sig-unsigned { font-size: 10px; color: #888; }
  .sig-date { font-size: 9px; color: #555; margin-top: 4px; }
  .sig-code-line { font-size: 7.5px; color: #666; font-family: "Courier New", monospace; margin-top: 3px; word-break: break-all; letter-spacing: 0.5px; }
  .vbadge { color: #003375; font-size: 9px; font-weight: 700; }

  .signed-seal {
    margin-top: 10px; text-align: center; color: #0a6a0a; font-size: 11px; font-weight: 700;
    border: 2px solid #0a6a0a; padding: 6px; letter-spacing: 1px;
  }

  /* Συμπαγές IP/συσκευή/επαλήθευση μέσα στο κουτί υπογραφής, αντί για
     ξεχωριστό πλατύ πίνακα μεταδεδομένων στο τέλος του εγγράφου. */
  .sig-meta-compact { font-size: 7px; color: #888; margin-top: 3px; line-height: 1.35; }

  /* ── Advance ── */
  .advance-table { width: 100%; border-collapse: collapse; margin-top: 6px; }
  .advance-table td { border: 1px solid #c8d8ec; padding: 4px 8px; font-size: 11px; vertical-align: top; }
  .advance-table td.lbl { background: #eaf0f9; font-weight: 700; width: 38%; font-size: 10.5px; color: #003375; }
  .advance-amount { font-weight: 700; color: #0a6a0a; }
  .advance-note { font-size: 10px; color: #555; font-style: italic; margin-top: 2px; }

  /* ── Σελιδοποίηση ── */
  tr { page-break-inside: avoid; break-inside: avoid; }
  .agreement-title-box { page-break-inside: avoid; break-inside: avoid; }
  .commitment-box { break-inside: avoid; }
  .parties { page-break-inside: avoid; break-inside: avoid; }
  .party { page-break-inside: avoid; break-inside: avoid; }
  .signed-seal { page-break-inside: avoid; break-inside: avoid; }
  .advance-item { page-break-inside: avoid; break-inside: avoid; }
  .sig-section-label { page-break-after: avoid; break-after: avoid; }
  .gov-band { page-break-inside: avoid; break-inside: avoid; }
  body { orphans: 3; widows: 3; }

  /* ── Footer ── */
  /* Χωρίς position:fixed: στο print engine του expo-print (WebView) το
     fixed δεν "κολλάει" αξιόπιστα στο κάτω άκρο της σελίδας — σε σύντομες
     συμφωνίες το footer κατέληγε αμέσως μετά το περιεχόμενο, αφήνοντας
     μεγάλο άδειο λευκό χώρο κάτω από αυτό. Τώρα ρέει κανονικά και σπρώχνεται
     στο κάτω μέρος της σελίδας μέσω του margin-top:auto στο flex body. */
  .footer {
    margin-top: auto;
    border-top: 2px solid #003375;
    padding: 4px 18px 5px;
    text-align: center;
    font-size: 8px;
    color: #666;
    line-height: 1.55;
    background: #fff;
  }
  .footer .gen { color: #555; display: block; }
  .footer .ic { font-family: "Courier New", monospace; font-size: 7.5px; letter-spacing: 1px; color: #003375; }
  .footer .disc { display: block; font-size: 7px; color: #aaa; margin-top: 1px; line-height: 1.4; }

  /* ── Εξώφυλλο επαγγελματία (σελίδα 1 της 2σέλιδης έκδοσης) ── */
  .cover-page { page-break-after: always; padding: 40px 32px; min-height: 240mm; }
  .cover-logo-wrap { text-align: center; margin-bottom: 24px; }
  .cover-logo-wrap img { max-height: 90px; max-width: 260px; object-fit: contain; }
  .cover-business-name { text-align: center; font-size: 18px; font-weight: 700; color: #003375; margin-bottom: 2px; }
  .cover-business-details { text-align: center; font-size: 10px; color: #666; margin-bottom: 8px; }
  .cover-subtitle { text-align: center; font-size: 10px; color: #666; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 28px; }
  .cover-title-bar { background: #1565C0; color: #fff; text-align: center; font-size: 12px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; padding: 8px 0; margin-bottom: 20px; }
  .cover-terms-box { border: 1px solid #c8d8ec; border-left: 4px solid #1565C0; background: #f8fbff; padding: 16px 20px; font-size: 11.5px; line-height: 1.7; color: #1a1a1a; }
  .cover-terms-box p, .cover-terms-box li { margin: 4px 0; }
  .cover-payment-box { margin-top: 16px; border: 1px solid #ddd; border-left: 4px solid #003375; background: #f9f9f9; padding: 14px 20px; }
  .cover-payment-label { font-size: 11px; font-weight: 700; color: #003375; margin-bottom: 6px; }
  .cover-payment-row { font-size: 11.5px; color: #1a1a1a; line-height: 1.7; word-break: break-all; }
  .cover-footer-note { margin-top: 24px; text-align: center; font-size: 8.5px; color: #999; }

  /* ── Σελίδα εμπορευμάτων (προσφορά τύπου «Εμπόρευμα») ── */
  .goods-page { page-break-after: always; padding: 40px 32px; min-height: 240mm; }
  .goods-title-bar { background: #003375; color: #fff; text-align: center; font-size: 12px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; padding: 8px 0; margin-bottom: 20px; }
  .goods-table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  .goods-table th { background: #eaf0f9; color: #003375; font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px; padding: 6px 8px; border: 1px solid #c8d8ec; text-align: left; }
  .goods-table td { padding: 6px 8px; border: 1px solid #c8d8ec; font-size: 10.5px; color: #1a1a1a; }
  .goods-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
  .goods-totals { margin-top: 14px; margin-left: auto; width: 260px; }
  .goods-totals-row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 11px; color: #444; }
  .goods-totals-row.final { border-top: 2px solid #003375; margin-top: 4px; padding-top: 8px; font-size: 13px; font-weight: 700; color: #003375; }

  /* Διαγώνιο υδατογράφημα ακυρωμένης συμφωνίας — position:fixed ώστε να
     επαναλαμβάνεται σε κάθε σελίδα του εκτυπωμένου PDF, όχι μόνο στην πρώτη. */
  .cancelled-watermark {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    z-index: 999;
  }
  .cancelled-watermark span {
    transform: rotate(-38deg);
    font-size: 90px;
    font-weight: 800;
    letter-spacing: 8px;
    color: rgba(220, 38, 38, 0.16);
    white-space: nowrap;
    text-transform: uppercase;
    border: 6px solid rgba(220, 38, 38, 0.16);
    padding: 10px 40px;
  }
</style>
</head>
<body>

  ${agreement.status?.startsWith('cancelled') ? '<div class="cancelled-watermark"><span>Ακυρώθηκε</span></div>' : ''}

  ${isGoodsOffer ? (() => {
    const items = (agreement as Agreement & { line_items: OfferLineItem[] }).line_items;
    const totals = computeLineItemsTotals(items);
    const goodsLogo = businessLogoBase64
      ? `<img src="data:image/jpeg;base64,${businessLogoBase64}" alt="${escapeHtml(businessName)}" style="max-height:70px;max-width:220px;object-fit:contain;" />`
      : (logoBase64 ? `<img src="data:image/png;base64,${logoBase64}" style="max-height:70px;max-width:220px;object-fit:contain;" alt="Χειραψία" />` : '');
    const rows = items.map((it) => {
      const discountAmount = lineItemDiscountAmount(it);
      const discountLabel = discountAmount > 0
        ? `-${formatAmount(discountAmount, agreement.currency)}${it.discountType === 'percent' ? ` (${it.discount}%)` : ''}`
        : '—';
      return `<tr>
        <td>${escapeHtml(it.description)}</td>
        <td class="num">${it.quantity}</td>
        <td class="num">${formatAmount(it.unitPrice, agreement.currency)}</td>
        <td class="num">${discountLabel}</td>
        <td class="num">${formatAmount(lineItemTotal(it), agreement.currency)}</td>
      </tr>`;
    }).join('');
    return `<div class="goods-page">
    <div class="cover-logo-wrap">${goodsLogo}</div>
    <div class="cover-business-name">${escapeHtml(businessName)}</div>
    ${businessDetailsHtml}
    <div class="goods-title-bar">Λίστα Εμπορευμάτων</div>
    <table class="goods-table">
      <tr><th>Προϊόν</th><th>Τεμ.</th><th>Τιμή Μονάδας</th><th>Έκπτωση</th><th>Σύνολο</th></tr>
      ${rows}
    </table>
    <div class="goods-totals">
      ${totals.discountTotal > 0 ? `
      <div class="goods-totals-row"><span>Σύνολο</span><span>${formatAmount(totals.subtotal, agreement.currency)}</span></div>
      <div class="goods-totals-row"><span>-Έκπτωση</span><span>-${formatAmount(totals.discountTotal, agreement.currency)}</span></div>
      <div class="goods-totals-row final"><span>Τελικό Σύνολο</span><span>${formatAmount(totals.finalTotal, agreement.currency)}</span></div>
      ` : `
      <div class="goods-totals-row final"><span>Συνολική τιμή</span><span>${formatAmount(totals.finalTotal, agreement.currency)}</span></div>
      `}
    </div>
  </div>`;
  })() : ''}

  ${isProfessionalVersion ? (() => {
    const coverLogo = businessLogoBase64
      ? `<img src="data:image/jpeg;base64,${businessLogoBase64}" alt="${escapeHtml(businessName)}" />`
      : (logoBase64 ? `<img src="data:image/png;base64,${logoBase64}" alt="Χειραψία" />` : '');
    return `<div class="cover-page">
    <div class="cover-logo-wrap">${coverLogo}</div>
    <div class="cover-business-name">${escapeHtml(businessName)}</div>
    ${businessDetailsHtml}
    <div class="cover-subtitle">Όροι Συνεργασίας</div>
    <div class="cover-title-bar">Πιστοποιητικό Ψηφιακής Συμφωνίας — Επαγγελματική Έκδοση</div>
    ${professionalTerms ? `<div class="cover-terms-box">${safeTermsToHtml(professionalTerms)}</div>` : ''}
    ${paymentMethodsHtml}
    <div class="cover-footer-note">Οι παρακάτω όροι έχουν οριστεί από τον/την ${escapeHtml(creatorName)} και ισχύουν για κάθε συμφωνία/προσφορά που συντάσσει μέσω Χειραψία. Ακολουθεί το πιστοποιητικό της συγκεκριμένης συμφωνίας.</div>
  </div>`;
  })() : ''}

  <!-- Gov.gr-style header band -->
  <div class="gov-band">
    <div class="gov-band-logo">
      ${logoBase64
        ? `<img src="data:image/png;base64,${logoBase64}" style="height:36px;width:auto;display:block;filter:brightness(10);" alt="Χειραψία" />`
        : ''}
      <div>
        <div class="gov-band-brand">ΧΕΙΡΑΨΙΑ</div>
        <div class="gov-band-sub">Ψηφιακές Συμφωνίες</div>
      </div>
    </div>
    <div class="gov-band-qr">
      ${qrMarkup}
      <div class="gov-band-qr-label">Επαλήθευση</div>
    </div>
  </div>
  <div class="doc-type-bar">Ψηφιακή Συμφωνία</div>

<div class="outer">

  <div class="doc-num-row">
    <span>Αριθμός Εγγράφου: <span class="doc-num">${escapeHtml(agreementDocNumber(agreement.id))}</span></span>
    <span>Κατάσταση: <strong>${escapeHtml(statusRow)}</strong></span>
    <span>Ημ/νία Δημιουργίας: <strong>${escapeHtml(formatDateTime(agreement.created_at))}</strong></span>
  </div>

  <!-- Τίτλος -->
  <div class="agreement-title-box">
    <div class="agreement-title-label">Τίτλος Συμφωνίας</div>
    <div class="agreement-title">${escapeHtml(agreement.title)}</div>
  </div>

  <!-- Πίνακας στοιχείων -->
  <table class="info-table">
    <tr><td class="lbl">Τύπος</td><td class="val">${escapeHtml(typeRow)}</td>
        <td class="lbl">${agreement.amount_unit === 'percent' ? 'Ποσοστό' : 'Ποσό'}</td>
        <td class="val">${escapeHtml(
          agreement.amount_unit === 'percent' && !isMultiParty
            ? `${creatorName}: ${formatAgreementAmount(agreement.amount, 'percent')} · ${counterpartyName}: ${formatAgreementAmount(agreement.amount != null ? Math.round((100 - Number(agreement.amount)) * 100) / 100 : null, 'percent')}`
            : formatAgreementAmount(agreement.amount, agreement.amount_unit, agreement.currency)
        )}</td></tr>
    <tr><td class="lbl">${escapeHtml(agrType === 'fixed_term' ? 'Περίοδος' : (agreement.expires_at_label ?? 'Ημερομηνία'))}</td>
        <td class="val">${escapeHtml(agrType === 'fixed_term'
          ? (agrStartDate && agrEndDate ? `${formatDate(agrStartDate)} – ${formatDate(agrEndDate)}` : agrStartDate ? `Από ${formatDate(agrStartDate)}` : 'Αορίστου')
          : (agreement.expires_at ? formatDate(agreement.expires_at) : 'Χωρίς λήξη'))}</td>
        <td class="lbl">Τελευταία ενημέρωση</td><td class="val">${escapeHtml(formatDateTime(agreement.updated_at))}</td></tr>
    ${isMultiParty
      ? `<tr><td class="lbl">Συμβαλλόμενοι (${multiPartySigs.length})</td><td class="val" colspan="3">${escapeHtml(multiPartyNamesJoined)}</td></tr>`
      : `<tr><td class="lbl">Δημιουργός</td><td class="val">${escapeHtml(creatorName)}</td>
        <td class="lbl">Αντισυμβαλλόμενος</td><td class="val">${escapeHtml(counterpartyName)}</td></tr>`}
  </table>

  <!-- Δέσμευση & Όροι -->
  <div class="commitment-box">
    <div class="commitment-label">Δέσμευση &amp; Όροι Συμφωνίας</div>
    <div class="commitment-text">${escapeHtml(isMultiParty
      ? `Οι συμβαλλόμενοι (${multiPartyNamesJoined}) δεσμεύονται αμοιβαία να τηρήσουν τους όρους της παρούσας συμφωνίας.`
      : commitmentIntro(creatorName, counterpartyName))}</div>
    ${agreement.description?.trim()
      ? `<div class="description-text">${escapeHtml(agreement.description).replace(/\n/g, '<br/>')}</div>`
      : ''}
  </div>

  ${(() => {
    // Στη 2σέλιδη «επαγγελματική» έκδοση οι όροι εμφανίζονται ήδη στο εξώφυλλο (σελίδα 1) — δεν επαναλαμβάνονται εδώ.
    if (isProfessionalVersion) return '';
    // Οι όροι επαγγελματία επισυνάπτονται ΜΟΝΟ όταν η συμφωνία προέρχεται από προσφορά
    // (include_professional_terms) — ποτέ σε χειροκίνητες συμφωνίες 1-προς-1/πολλών ατόμων.
    if (!includeProfessionalTerms) return '';
    const terms = (creatorProfile as Profile & { professional_terms?: string | null } | undefined)?.professional_terms?.trim();
    return terms
      ? `<div class="commitment-box">
    <div class="commitment-label">Όροι &amp; Προϋποθέσεις ${escapeHtml(creatorName)}</div>
    <div class="description-text">${safeTermsToHtml(terms)}</div>
  </div>`
      : '';
  })()}

  ${advanceHtml}

  <!-- Υπογραφές -->
  <div class="sig-section-label">Ψηφιακές Υπογραφές Συμβαλλομένων</div>
  <div class="parties">
    ${isMultiParty
      ? multiPartySigs.map((s) => signatureBlock(s)).join('')
      : `${signatureBlock(creatorSig)}${signatureBlock(counterpartySig)}`}
  </div>
  ${bothSigned ? `<div class="signed-seal">✓ ΨΗΦΙΑΚΑ ΥΠΟΓΕΓΡΑΜΜΕΝΟ ΑΠΟ ${isMultiParty ? 'ΟΛΑ ΤΑ ΜΕΡΗ' : 'ΑΜΦΟΤΕΡΑ ΤΑ ΜΕΡΗ'}</div>` : ''}

</div>

<div class="footer">
  <span class="gen">Δημιουργήθηκε ψηφιακά μέσω Χειραψία στις ${escapeHtml(generatedAt)} · Κωδικός ακεραιότητας: <span class="ic">${escapeHtml(integrityCode)}</span></span>
  <span class="disc">Το παρόν έγγραφο δημιουργήθηκε μέσω της πλατφόρμας Χειραψία και αποτελεί ψηφιακή καταγραφή συμφωνίας μεταξύ ιδιωτών. Δεν συνιστά συμβολαιογραφικό έγγραφο ούτε Εγκεκριμένη Ηλεκτρονική Υπογραφή κατά τον κανονισμό eIDAS. Η Χειραψία δεν φέρει ευθύνη για το περιεχόμενο, τη νομιμότητα ή την εκτέλεση της συμφωνίας. Κάθε συμβαλλόμενος φέρει την αποκλειστική ευθύνη για τις φορολογικές του υποχρεώσεις βάσει του Ν. 4172/2013. Εφαρμοστέο δίκαιο: Ελληνικό.</span>
</div>

</body>
</html>`;
}

/**
 * Παράγει το PDF πιστοποιητικό της συμφωνίας και το ΑΝΟΙΓΕΙ για εκτύπωση/αποθήκευση:
 * - native: δημιουργεί αρχείο PDF και το ανοίγει στον viewer (share → εκτύπωση/αποθήκευση)
 * - web: ανοίγει το έγγραφο σε νέα καρτέλα και καλεί τον διάλογο εκτύπωσης
 */
/** Διαβάζει τοπικό (file://) ή απομακρυσμένο (http(s)) URI εικόνας και επιστρέφει base64, για ενσωμάτωση στο PDF. */
async function uriToBase64(uri: string): Promise<string | null> {
  try {
    if (Platform.OS === 'web') {
      const res = await fetch(uri);
      const blob = await res.blob();
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1] ?? '');
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    }
    if (uri.startsWith('http://') || uri.startsWith('https://')) {
      const downloaded = await File.downloadFileAsync(uri, Paths.cache);
      const base64 = await downloaded.base64();
      downloaded.delete();
      return base64;
    }
    return await new File(uri).base64();
  } catch (e) {
    console.warn('uriToBase64 failed for PDF logo:', e);
    return null;
  }
}

export async function exportAgreementPdf(
  agreement: Agreement,
  profiles: Record<string, Profile>,
  advance?: PdfAdvance,
  multiParties?: AgreementParty[],
): Promise<void> {
  const events = await fetchSigningEvents(agreement.id).catch(() => []);

  let logoBase64: string | undefined;
  try {
    const [asset] = await Asset.loadAsync(require('../../assets/logo.png'));
    const uri = asset.localUri ?? asset.uri;
    if (uri) {
      logoBase64 = (await uriToBase64(uri)) ?? undefined;
    }
  } catch (e) {
    console.warn('Default logo load failed for PDF:', e);
    /* το PDF φαίνεται και χωρίς εικόνα */
  }

  const creatorProfileForLogo = profiles[agreement.creator_id] as Profile & { professional_terms?: string | null; business_logo_url?: string | null } | undefined;
  const isGoodsOfferForLogo = (agreement as Agreement & { offer_kind?: string }).offer_kind === 'goods';
  let businessLogoBase64: string | null = null;
  if ((creatorProfileForLogo?.professional_terms?.trim() || isGoodsOfferForLogo) && creatorProfileForLogo?.business_logo_url) {
    businessLogoBase64 = await uriToBase64(creatorProfileForLogo.business_logo_url);
  }

  const html = buildHtml(agreement, profiles, advance, events, logoBase64, businessLogoBase64, multiParties);

  if (Platform.OS === 'web') {
    // Άνοιξε ΤΟ ΕΓΓΡΑΦΟ (όχι την οθόνη) σε νέα καρτέλα και κάλεσε εκτύπωση.
    const win = typeof window !== 'undefined' ? window.open('', '_blank') : null;
    if (win) {
      win.document.write(html);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 400);
    } else {
      // Popup blocked → fallback στο expo-print
      await Print.printAsync({ html });
    }
    return;
  }

  // native: δημιουργία αρχείου PDF από το HTML (όχι από την οθόνη)
  const { uri } = await Print.printToFileAsync({ html });
  if (await Sharing.isAvailableAsync()) {
    await Sharing.shareAsync(uri, {
      mimeType: 'application/pdf',
      dialogTitle: 'Πιστοποιητικό συμφωνίας',
      UTI: 'com.adobe.pdf',
    });
  } else {
    // Αν δεν υπάρχει sharing, άνοιξε απευθείας τον διάλογο εκτύπωσης για το αρχείο.
    await Print.printAsync({ uri });
  }
}
